import { usePrimaryTravelerForBooking } from "@myaccount/booking/[bookingId]/_client/hooks/use-primary-traveler-for-booking";
import { I18nHive, useI18n } from "@wojo/localization";
import {
    CheckListItemImportanceLevel,
    MyAccountTripWithChecklistFragment,
    SystemChecklistItem,
    TripChecklistItem,
    TripChecklistItemStatus,
} from "generated/graphql";
import { useBookingTripForChecklist } from "./useTrips";

export type ChecklistItem = {
    description: string;
    dueDate: string;
    id: string;
    checklistItemTemplateId: string;
    importanceLevel: CheckListItemImportanceLevel;
    isChecked: boolean;
    status: TripChecklistItemStatus;
    title: string;
    isSystemItem?: boolean;
    actionText?: string;
    actionUrl?: string;
    tripId?: string;
};

export type GroupedChecklists = {
    required: ChecklistItem[];
    recommended: ChecklistItem[];
    complete: ChecklistItem[];
};

export type SystemChecklistItemType =
    | "EmergencyContactChecklistItem"
    | "PassportInformationChecklistItem"
    | "TermsSignatureChecklistItem"
    | "TsaIdentifiersChecklistItem"
    | "HealthNotesChecklistItem";

export enum ChecklistViewMode {
    PrimaryViewingOther = "PrimaryViewingOther",
    SecondaryViewingOther = "SecondaryViewingOther",
}

const toSystemChecklistItem = <
    T extends SystemChecklistItem & { __typename?: SystemChecklistItemType },
>(
    item: T,
    bookingId: string,
    rootL10n: I18nHive<"account">["account"],
    viewMode: ChecklistViewMode,
    customerFirstName: string,
): ChecklistItem | [] => {
    if (!item.dueDate || !item.status || !item.__typename) {
        return [];
    }
    const l10n = rootL10n.checklist.system;
    const bookingL10n = rootL10n.navigation.booking;
    const partialChecklistItem = {
        id: item.__typename,
        checklistItemTemplateId: item.__typename,
        dueDate: item.dueDate,
        isChecked: item.isCompleted,
        status: item.status,
        importanceLevel: CheckListItemImportanceLevel.Required,
        isSystemItem: true,
    };

    switch (item.__typename) {
        case "EmergencyContactChecklistItem":
            return {
                ...partialChecklistItem,
                id: item.__typename,
                title: l10n.emergencyContact.title.l(),
                description: l10n.emergencyContact.description.l(),
                actionText: l10n.emergencyContact.action.l(),
                actionUrl: bookingL10n.bookingLink.l({
                    bookingId,
                    slug: bookingL10n.travelerDetails.slug.l(),
                }),
            };
        case "PassportInformationChecklistItem":
            return {
                ...partialChecklistItem,
                id: item.__typename,
                title: l10n.passportInformation.title.l(),
                description: l10n.passportInformation.description.l(),
                actionText: l10n.passportInformation.action.l(),
                actionUrl: bookingL10n.bookingLink.l({
                    bookingId,
                    slug: bookingL10n.travelerDetails.slug.l(),
                }),
            };
        case "TermsSignatureChecklistItem":
            return {
                ...partialChecklistItem,
                id: item.__typename,
                title: l10n.acceptTerms.title.l(),
                description:
                    viewMode === ChecklistViewMode.PrimaryViewingOther
                        ? l10n.acceptTerms.primaryViewingOther.l({
                              customerName: customerFirstName,
                          })
                        : l10n.acceptTerms.secondaryViewingOther.l({
                              customerName: customerFirstName,
                          }),
                actionText: l10n.acceptTerms.action.l(),
                actionUrl: bookingL10n.bookingLink.l({
                    bookingId,
                    slug: bookingL10n.travelerDetails.slug.l(),
                }),
            };
        case "TsaIdentifiersChecklistItem":
            return {
                ...partialChecklistItem,
                id: item.__typename,
                title: l10n.completeTSAInformation.title.l(),
                description: l10n.completeTSAInformation.description.l(),
                actionText: l10n.completeTSAInformation.action.l(),
                actionUrl: bookingL10n.bookingLink.l({
                    bookingId,
                    slug: bookingL10n.travelerDetails.slug.l(),
                }),
            };
        case "HealthNotesChecklistItem":
            return {
                ...partialChecklistItem,
                id: item.__typename,
                title: l10n.healthNotes.title.l(),
                description: l10n.healthNotes.description.l(),
                actionText: l10n.healthNotes.action.l(),
                actionUrl: bookingL10n.bookingLink.l({
                    bookingId,
                    slug: bookingL10n.travelerDetails.slug.l(),
                }),
            };
    }
};

const toManualChecklistItem = <T extends TripChecklistItem>(
    item: T,
    bookingId: string,
    rootL10n: I18nHive<"account">["account"],
): ChecklistItem | [] => {
    if (!item.dueDate || !item.__typename) {
        return [];
    }
    const l10n = rootL10n.checklist.manual;
    const navigationL10n = rootL10n.navigation;
    const partialChecklistItem = {
        ...item,
        isSystemItem: false,
    };

    switch (item.internalName) {
        case "DownloadMobileApp":
            return {
                ...partialChecklistItem,
                actionText: l10n.downloadMobileApp.action.l(),
                actionUrl: navigationL10n.downloadMobileApp.url.l(),
            };
        case "EntryRequirements":
            return {
                ...partialChecklistItem,
                actionText: l10n.entryRequirements.action.l(),
                actionUrl: navigationL10n.entryRequirements.url.l(),
            };
        case "GettingReadyFAQ":
            return {
                ...partialChecklistItem,
                actionText: l10n.gettingReadyFaq.action.l(),
                actionUrl: navigationL10n.booking.bookingLink.l({
                    bookingId,
                    slug: navigationL10n.booking.gettingReady.slug.l(),
                }),
            };
        case "MailingAddress":
            return {
                ...partialChecklistItem,
                actionText: l10n.mailingAddress.action.l(),
                actionUrl: navigationL10n.profile.url.l(),
            };
        case "PurchaseTripExcursions":
            return {
                ...partialChecklistItem,
                actionText: l10n.purchaseTripExcursions.action.l(),
                actionUrl: navigationL10n.booking.bookingLink.l({
                    bookingId,
                    slug: navigationL10n.booking.customizationOptions.slug.l(),
                }),
            };
        case "PurchaseTripProtection":
            return {
                ...partialChecklistItem,
                actionText: l10n.purchaseTripProtection.action.l(),
                actionUrl: navigationL10n.booking.bookingLink.l({
                    bookingId,
                    slug: navigationL10n.booking.travelProtection.slug.l(),
                }),
            };
        case "ReviewEntryRequirements":
            return {
                ...partialChecklistItem,
                actionText: l10n.reviewEntryRequirements.action.l(),
                actionUrl: navigationL10n.entryRequirements.url.l(),
            };
        case "TravelerRulesOfTheRoad":
            return {
                ...partialChecklistItem,
                actionText: l10n.travelerRulesOfTheRoad.action.l(),
                actionUrl: navigationL10n.travelerRulesOfTheRoad.url.l(),
            };
        case "VisitKnowBeforeYouGo":
            return {
                ...partialChecklistItem,
                actionText: l10n.visitKnowBeforeYouGo.action.l(),
                actionUrl: navigationL10n.booking.bookingLink.l({
                    bookingId,
                    slug: navigationL10n.booking.gettingReady.slug.l(),
                }),
            };
        case "ArrivalDayGuide":
            return {
                ...partialChecklistItem,
                actionText: l10n.arrivalDayGuide.action.l(),
                actionUrl: navigationL10n.arrivalDayGuide.url.l(),
            };
        default:
            return partialChecklistItem;
    }
};

const split = (acc: GroupedChecklists, item: ChecklistItem) => {
    switch (item.status) {
        case TripChecklistItemStatus.DueInFuture:
        case TripChecklistItemStatus.Overdue:
            switch (item.importanceLevel) {
                case CheckListItemImportanceLevel.Required:
                    acc["required"].push(item);
                    break;
                case CheckListItemImportanceLevel.HighlyRecommended:
                case CheckListItemImportanceLevel.Recommended:
                    acc["recommended"].push(item);
                    break;
            }
            break;
        case TripChecklistItemStatus.Complete:
            acc["complete"].push(item);
            break;
    }
    return acc;
};

export const getAllChecklistItems = (
    trip: MyAccountTripWithChecklistFragment,
    bookingId: string,
    l10n: I18nHive<"account">["account"],
    viewMode: ChecklistViewMode,
    customerFirstName: string,
): ChecklistItem[] => {
    return [
        ...getSystemCheckListItems(trip).map((systemItem) =>
            toSystemChecklistItem(
                systemItem,
                bookingId,
                l10n,
                viewMode,
                customerFirstName,
            ),
        ),
        ...trip.checklistItems.map((manualItem) =>
            toManualChecklistItem(manualItem, bookingId, l10n),
        ),
    ]
        .flat()
        .map((item) => ({
            ...item,
            tripId: trip._id,
        }));
};

export const getSystemCheckListItems = (
    trip: MyAccountTripWithChecklistFragment,
) =>
    [
        trip.emergencyContactChecklistItem ?? [],
        trip.passportInformationChecklistItem ?? [],
        trip.termsSignatureChecklistItem ?? [],
        trip.tsaIdentifiersChecklistItem ?? [],
        trip.healthNotesChecklistItem ?? [],
    ].flat();

export const useChecklist = (bookingId: string, tripId: string) => {
    const trip = useBookingTripForChecklist(tripId);
    const primaryInfo = usePrimaryTravelerForBooking(bookingId);
    const customer = trip?.customer;
    const l10n = useI18n("account").account;

    if (!customer || !customer.firstName) {
        return null;
    }

    const { firstName, lastName } = customer;
    const lastInitial = lastName ? lastName[0] : "";

    const viewMode =
        primaryInfo?.isLoggedInAsPrimaryTraveler &&
        primaryInfo.primaryTraveler?._id !== customer._id
            ? ChecklistViewMode.PrimaryViewingOther
            : ChecklistViewMode.SecondaryViewingOther;

    const checklistItems = getAllChecklistItems(
        trip,
        bookingId,
        l10n,
        viewMode,
        firstName,
    );

    return {
        firstName,
        lastInitial,
        items: checklistItems.reduce(split, {
            required: [],
            recommended: [],
            complete: [],
        }),
    };
};
